"use client";

import { Cable, Eye, Layers3 } from "lucide-react";
import { motion } from "framer-motion";
import { Container } from "@/components/ui/Container";
import { usePrefersReducedMotion } from "@/lib/motion";

const problems = [
  {
    icon: Eye,
    title: "Falhas descobertas tarde demais",
    description: "Ganhe visibilidade da operação e identifique sinais de problema antes que virem reclamações.",
  },
  {
    icon: Cable,
    title: "Fornecedores e sistemas desconectados",
    description: "Conecte conteúdo, infraestrutura, ERPs e monitoramento em uma operação mais coordenada.",
  },
  {
    icon: Layers3,
    title: "Crescimento sem previsibilidade",
    description: "Planeje capacidade, implantação e evolução para crescer sem reconstruir tudo a cada etapa.",
  },
] as const;

export function BuiltForScale() {
  const reduced = usePrefersReducedMotion();

  return (
    <section className="relative overflow-hidden py-20 sm:py-24">
      <div aria-hidden className="bg-grid pointer-events-none absolute inset-0 opacity-10" />
      <Container className="relative">
        <div className="mx-auto max-w-3xl text-center">
          <p className="font-mono text-xs uppercase tracking-[0.12em] text-accent-lime">
            Problemas que resolvemos
          </p>
          <h2 className="mt-3 text-4xl font-black uppercase italic leading-[0.95] tracking-tight text-text-primary sm:text-5xl">
            Menos risco operacional. <span className="text-accent-lime">Mais controle para crescer.</span>
          </h2>
          <p className="mt-5 text-base text-text-secondary sm:text-lg">
            Unimos tecnologia e acompanhamento especializado para simplificar uma operação que não pode parar.
          </p>
        </div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.12 } } }}
          className="mt-12 grid gap-5 md:grid-cols-3"
        >
          {problems.map((problem) => {
            const Icon = problem.icon;
            return (
              <motion.article
                key={problem.title}
                variants={
                  reduced
                    ? { hidden: { opacity: 0 }, show: { opacity: 1 } }
                    : { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }
                }
                className="rounded-xl border border-line bg-bg-raised p-7"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-accent-lime/40 text-accent-lime">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-5 text-lg font-bold uppercase tracking-tight text-text-primary">
                  {problem.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-text-secondary">{problem.description}</p>
              </motion.article>
            );
          })}
        </motion.div>
      </Container>
    </section>
  );
}
